import { takeEvery, call, select, take, put } from 'redux-saga/effects';
import intersection from 'lodash.intersection';
import get from 'lodash.get';
import queryString from 'query-string';
import { LOCATION_CHANGE } from 'react-router-redux';
import actionKey from '../utils/action-key';
import request from '../api-utils/request';
import {
    TOGGLE_FILTER,
    TOGGLE_FILTER_STATE,
    RESET_FILTERS,
    FILTER_PROGRAMS,
    resetFiltersAction,
    toggleFilterAction,
} from './filter';
import onetIDToSubjectArea from '../utils/occupationToSubjectArea';
import subjectAreas from '../utils/subjectAreas';
import { FETCH_COLLEGE_PROGRAMS } from './college-programs';

export const FETCH_CAREER_MATCHES = 'career/FETCH_CAREER_MATCHES';
export const FETCH_CAREER_MATCH = 'career/FETCH_CAREER_MATCH';
export const GET_CAREER_MATCH = 'career/GET_CAREER_MATCH';
export const FETCH_CAREER_DETAILS = 'career/FETCH_CAREER_DETAILS';
export const FETCH_OCCUPATION_INDUSTRY = 'career/FETCH_OCCUPATION_INDUSTRY';
export const FILTER_MATCHES = 'career/FILTER_MATCHES';
export const SEARCH_MATCHES = 'career/SEARCH_MATCHES';
export const CHANGE_VIEW = 'career/CHANGE_VIEW';

export default (state = {
    careerDetails: null,
    careerMatches: [],
    careerMatch: null,
    programsByCareer: [],
    currentUserId: null,
    error: false,
    filteredMatches: [],
    loading: false,
    loadingDetails: false,
    occupationToIndustry: {},
    occupationToIndustryLoaded: false,
    occupationToIndustryLoading: false,
    searchString: '',
    view: 'subject-areas',
}, action) => {
    switch (action.type) {
        case FETCH_CAREER_MATCHES: {
            return {
                ...state,
                loading: true,
                currentUserId: action.userId,
            };
        }
        case actionKey.success(FETCH_CAREER_MATCHES): {
            const result = mergeMissingFields(
                action.payload.matches, state.occupationToIndustry);

            return {
                ...state,
                careerMatches: result,
                loading: false,
            };
        }
        case actionKey.success(FETCH_CAREER_MATCH): {
            return {
                ...state,
                programsByCareer: action.payload.programs,
                careerMatch: mergeSubjectArea(action.payload.match),
            };
        }
        case GET_CAREER_MATCH: {
            return {
                ...state,
                careerMatch: state.careerMatches
                    .find(c => c.occupationID.toString() === action.occupationId),
            };
        }
        case FETCH_CAREER_DETAILS: {
            return {
                ...state,
                error: false,
                loadingDetails: true,
            };
        }
        case actionKey.success(FETCH_CAREER_DETAILS): {
            return {
                ...state,
                careerDetails: action.payload,
                error: false,
                loadingDetails: false,
            };
        }
        case actionKey.failure(FETCH_CAREER_DETAILS): {
            return {
                ...state,
                careerDetails: null,
                error: true,
                loadingDetails: false,
            };
        }
        case FILTER_MATCHES: {
            const result = filterMatches(state.careerMatches, action.filters);
            return {
                ...state,
                filteredMatches: result,
            };
        }
        case SEARCH_MATCHES: {
            return {
                ...state,
                searchString: action.searchString,
            };
        }
        case FETCH_OCCUPATION_INDUSTRY: {
            return {
                ...state,
                occupationToIndustryLoading: true,
            };
        }
        case actionKey.success(FETCH_OCCUPATION_INDUSTRY): {
            return {
                ...state,
                occupationToIndustryLoading: false,
                occupationToIndustryLoaded: true,
                occupationToIndustry: action.payload.reduce((acc, current) => {
                    acc[current.occupationID] = acc[current.occupationID]
                        ? acc[current.occupationID] =
                          acc[current.occupationID].concat(current.industryID)
                        : acc[current.occupationID] = [current.industryID];
                    return acc;
                }, {}),
            };
        }
        case actionKey.failure(FETCH_OCCUPATION_INDUSTRY): {
            return {
                ...state,
                occupationToIndustryLoading: false,
                occupationToIndustry: {},
            };
        }
        case RESET_FILTERS: {
            return {
                ...state,
                searchString: '',
            };
        }
        case CHANGE_VIEW:
            return {
                ...state,
                view: action.payload,
                searchString: '',
            };
        case LOCATION_CHANGE: {
            if (state.view !== 'subject-areas' &&
                action.payload.pathname.indexOf('/career-matches') === -1 &&
                action.payload.pathname.indexOf('/career-details') === -1
            ) {
                return {
                    ...state,
                    view: 'subject-areas',
                };
            }
            return state;
        }
        default:
            return state;
    }
};

/**
 * REDUX ACTIONS
 */

export const fetchCareerMatchesAction = userId => ({
    type: FETCH_CAREER_MATCHES,
    userId,
});

export const changeViewAction = payload => ({
    type: CHANGE_VIEW,
    payload,
});

export const fetchCareerMatchAction = (userId, occupationId) => ({
    type: FETCH_CAREER_MATCH,
    occupationId,
    userId,
});

export const getCareerMatchAction = occupationId => ({
    type: GET_CAREER_MATCH,
    occupationId,
});

export const fetchCareerDetailsAction = (occupationId, onetId) => ({
    type: FETCH_CAREER_DETAILS,
    onetId,
    occupationId,
});

export const fetchOccupationToIndustryAction = () => ({
    type: FETCH_OCCUPATION_INDUSTRY,
});

export const searchMatchesAction = searchString => ({
    type: SEARCH_MATCHES,
    searchString,
});

/**
 * SAGA LISTENERS
 */

function* lisenFetchCareerMatches() {
    yield takeEvery(FETCH_CAREER_MATCHES, fetchCareerMatches);
}

function* lisenFetchCareerMatch() {
    yield takeEvery(FETCH_CAREER_MATCH, fetchCareerMatch);
}

function* listenFetchCareerDetails() {
    yield takeEvery(FETCH_CAREER_DETAILS, fetchCareerDetails);
}

function* listenFilterEvents() {
    yield takeEvery([
        actionKey.success(FETCH_CAREER_MATCHES),
        actionKey.success(FETCH_COLLEGE_PROGRAMS),
        RESET_FILTERS,
        CHANGE_VIEW,
        SEARCH_MATCHES,
        TOGGLE_FILTER,
        TOGGLE_FILTER_STATE,
    ], filterEvent);
}

function* listenFetchOccupationToIndustry() {
    yield takeEvery(FETCH_OCCUPATION_INDUSTRY, fetchOccupationToIndustry);
}

export function* rootSaga() {
    yield [
        lisenFetchCareerMatches(),
        lisenFetchCareerMatch(),
        listenFetchCareerDetails(),
        listenFilterEvents(),
        listenFetchOccupationToIndustry(),
        takeEvery(LOCATION_CHANGE, filterOnCollegeProgramRoute),
    ];
}

/**
 * REDUX SAGAS
 */

function* fetchCareerMatches({ userId, type }) {
    const { career: { occupationToIndustryLoading } } = yield select();

    if (occupationToIndustryLoading) {
        yield take([
            actionKey.success(FETCH_OCCUPATION_INDUSTRY),
            actionKey.failure(FETCH_OCCUPATION_INDUSTRY),
        ]);
    }
    yield call(request, {
        url: `/api/career/matches?userId=${userId}`,
        type,
        userId,
        options: {
            method: 'GET',
        },
    });
}

function* fetchCareerMatch({ userId, occupationId, type }) {
    yield call(request, {
        url: `/api/career/match?userId=${userId}&occupationId=${occupationId}`,
        type,
        options: {
            method: 'GET',
        },
    });
}

function* fetchCareerDetails({ onetId, occupationId, type }) {
    yield call(request, {
        url: `/api/career/details?onetId=${onetId}&occupationId=${occupationId}`,
        type,
        options: {
            method: 'GET',
        },
    });
}

function* fetchOccupationToIndustry({ type }) {
    const { career: { occupationToIndustryLoaded } } = yield select();

    if (!occupationToIndustryLoaded) {
        yield call(request, {
            url: '/api/industries/occupations',
            type,
            options: {
                method: 'GET',
            },
        });
    }
}

function* filterEvent() {
    const {
        filter,
        career: { searchString },
        favoriteCareers: favorites,
        theme: { customProgramGeneral },
        collegePrograms,
    } = yield select();

    const filters = {
        jobZone: filter.jobZone,
        subjectArea: filter.subjectArea,
        matchStrength: filter.matchStrength,
        industry: filter.industry,
        searchString,
        favorite: filter.favorite,
        favorites,
        programs: Object.keys(filter.programs).reduce((acc, id) => {
            const str = customProgramGeneral ? 'listCustom' : 'list';
            const collegeProgram = get(collegePrograms, str, []).find(p => (
                Number(p.id) === Number(id)
            ));
            if (!collegeProgram) {
                return acc;
            }
            collegeProgram.occupations.forEach((occ) => {
                acc[occ] = true;
            });
            return acc;
        }, {}),
    };

    yield put({ type: FILTER_MATCHES, filters });
}

function* filterOnCollegeProgramRoute({ payload: { search } }) {
    const query = queryString.parse(search);
    if (!query.program) {
        return;
    }
    const id = query.program;
    let state = yield select();
    if (id) {
        yield put(changeViewAction('careers'));
    }
    let program = get(state, 'collegePrograms.list', []).find(p => Number(p.id) === Number(id));
    if (!program) {
        yield take(actionKey.success(FETCH_COLLEGE_PROGRAMS));
        state = yield select();
        program = get(state, 'collegePrograms.list', []).find(p => Number(p.id) === Number(id));
        if (!program) {
            return;
        }
    }
    yield put(resetFiltersAction());
    yield put(toggleFilterAction({
        filterId: FILTER_PROGRAMS,
        filterOptionId: program.id.toString(),
        label: program.name,
        selected: false,
    }));
}

/**
 * PRIVATE FUNCTIONS
 */

const mergeMissingFields = (careerMatches, occupationToIndustry) => (
    careerMatches.map((careerMatch) => {
        const subjectAreaID = onetIDToSubjectArea[careerMatch.onetID];

        return {
            ...careerMatch,
            industryIDs: occupationToIndustry[careerMatch.occupationID],
            subjectAreaID,
            subjectAreaName: subjectAreas[subjectAreaID],
        };
    })
);

const mergeSubjectArea = (careerMatch) => {
    const subjectAreaID = onetIDToSubjectArea[careerMatch.onetID];
    return {
        ...careerMatch,
        subjectAreaID,
        subjectAreaName: subjectAreas[subjectAreaID],
    };
};

const filterMatches = (
    careerMatches,
    { jobZone, subjectArea, industry, matchStrength,
        searchString, favorite, favorites, programs },
) => (
    careerMatches
        .filter(m => filterContainsField(jobZone, m.jobZoneID))
        .filter(m => filterContainsField(subjectArea, m.subjectAreaID))
        .filter(m => filterContainsField(matchStrength, m.comboMatchQual))
        .filter(m => filterContainsField(industry, m.industryIDs))
        .filter(m => search(searchString, m))
        .filter(m => (favorite && favorite.selected ? favorites[m.occupationID] : true))
        .filter(m => Object.keys(programs).length === 0 || programs[m.occupationID])
);

const filterContainsField = (filter, field) => {
    if (typeof filter === 'undefined' || Object.keys(filter).length < 1) return true; // Filter empty, so ignore this filter.
    if (field === null || typeof field === 'undefined') {
        return false; // Incomplete data, so skip this match.
    }

    const selectedFilterOptions = Object.keys(filter).reduce((acc, filterKey) => {
        if (filter[filterKey].selected) acc.push(filterKey);
        return acc;
    }, []);

    // Nothing selected, so ignore this filter.
    if (selectedFilterOptions.length < 1) return true;

    if (Array.isArray(field)) {
        return intersection(selectedFilterOptions,
            field.map(f => f.toString())).length > 0;
    }

    return selectedFilterOptions.indexOf(field.toString()) !== -1;
};

const search = (searchString, { occupationTitle, description, occupationAlternativeTitles }) => {
    if (!searchString) return true;

    const query = searchString.toLowerCase();
    const title = occupationTitle ? occupationTitle.toLowerCase() : '';
    const desc = description ? description.toLowerCase() : '';
    const altTitles = occupationAlternativeTitles
        ? occupationAlternativeTitles.toLowerCase() : '';

    return title.includes(query) || desc.includes(query) || altTitles.includes(query);
};



// WEBPACK FOOTER //
// ./src/client/redux/career.js